/**
 * <s-carousel> — a horizontally-scrollable "swim lane" that shows multiple items at once.
 *
 * UI-only: it wires every prev/next button inside it to scroll the track by roughly one page
 * and toggles their `disabled` state at the start/end of the scroll range. The track is a
 * native scroll-snap container, so scrolling, touch/trackpad swiping, and keyboard focus
 * all work without JS — this component only enhances the arrow controls.
 *
 * Expected DOM:
 *   <s-carousel>
 *     [<button data-carousel-prev> / <button data-carousel-next>]  (any number of each)
 *     <div data-carousel-track> …items… </div>
 *   </s-carousel>
 */
class SCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-carousel-track]");
    // A carousel can carry more than one arrow set (e.g. desktop header + mobile footer).
    this.prevButtons = [...this.querySelectorAll("[data-carousel-prev]")];
    this.nextButtons = [...this.querySelectorAll("[data-carousel-next]")];

    if (!this.track) return;

    this.controller = new AbortController();
    const { signal } = this.controller;

    this.prevButtons.forEach((button) => button.addEventListener("click", () => this.scrollByPage(-1), { signal }));
    this.nextButtons.forEach((button) => button.addEventListener("click", () => this.scrollByPage(1), { signal }));
    this.track.addEventListener("scroll", () => this.updateControls(), { passive: true, signal });

    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);

    this.updateControls();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
  }

  /**
   * Scroll the track by ~80% of its visible width in the given direction.
   * @param {number} direction -1 for previous, 1 for next.
   */
  scrollByPage(direction) {
    const rtl = getComputedStyle(this.track).direction === "rtl";
    const amount = this.track.clientWidth * 0.8 * direction * (rtl ? -1 : 1);
    this.track.scrollBy({ left: amount, behavior: "smooth" });
  }

  /** Disable prev/next when the track is at its respective end. */
  updateControls() {
    if (!this.prevButtons.length && !this.nextButtons.length) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const position = Math.abs(this.track.scrollLeft);
    const atStart = position <= 1;
    const atEnd = position >= maxScroll - 1;
    const overflowing = maxScroll > 1;

    this.prevButtons.forEach((button) => (button.disabled = !overflowing || atStart));
    this.nextButtons.forEach((button) => (button.disabled = !overflowing || atEnd));
  }
}

if (!customElements.get("s-carousel")) {
  customElements.define("s-carousel", SCarousel);
}
